// Brain Dump Page - Standalone data intake for capturing anything
// Mobile-first, minimal UI, no nav, no clutter

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  brainDumpService,
  type DumpSubmissionSummary,
  type SalespersonOption,
} from '../services/brainDump';
import { BrainDumpVoiceRecorder } from '../components/brain-dump/BrainDumpVoiceRecorder';
import { LargeFileUploader } from '../components/brain-dump/LargeFileUploader';
import type { UploadCompleteResponse } from '../services/largeFileUpload';
import styles from './BrainDumpPage.module.css';

interface Recording {
  id: string;
  blob: Blob;
  duration: number;
  url: string;
}

const OTHER_SALESPERSON_VALUE = '__other__';

export function BrainDumpPage() {
  // Input state
  const [textContent, setTextContent] = useState('');
  const [recordings, setRecordings] = useState<Recording[]>([]);

  // Salesperson state
  const [salespeople, setSalespeople] = useState<SalespersonOption[]>([]);
  const [selectedSalesperson, setSelectedSalesperson] = useState('');
  const [customSalesperson, setCustomSalesperson] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [recentSubmissions, setRecentSubmissions] = useState<DumpSubmissionSummary[]>([]);
  const [showRecent, setShowRecent] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load recent submissions and salespeople on mount
  useEffect(() => {
    loadRecent();
    loadSalespeople();
  }, []);

  const loadRecent = async () => {
    try {
      const subs = await brainDumpService.listSubmissions(10);
      setRecentSubmissions(subs);
    } catch {
      // Silently fail - not critical
    }
  };

  const loadSalespeople = async () => {
    try {
      const sp = await brainDumpService.getSalespeople();
      setSalespeople(sp);
    } catch {
      // Silently fail - dropdown will just be empty
    }
  };

  // Resolve the effective salesperson_id to send to the API
  const getEffectiveSalespersonId = (): string | null => {
    if (!selectedSalesperson) return null;
    if (selectedSalesperson === OTHER_SALESPERSON_VALUE) {
      return customSalesperson.trim() || null;
    }
    return selectedSalesperson;
  };

  // Large file upload completion handler
  const handleLargeUploadComplete = useCallback(
    (result: UploadCompleteResponse) => {
      setSuccessMessage(result.message);
      setTextContent('');
      setRecordings([]);
      loadRecent();
    },
    [],
  );

  const handleUploadError = useCallback((message: string) => {
    setError(message);
  }, []);

  // Submit (text + audio only - files go through LargeFileUploader)
  const handleSubmit = async () => {
    const hasContent =
      textContent.trim() || recordings.length > 0;

    if (!hasContent) {
      setError('Add some content first - type something or record audio.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const audioFiles = recordings.map(
        (r, i) =>
          new File([r.blob], `recording-${i + 1}.webm`, { type: 'audio/webm' }),
      );

      const result = await brainDumpService.upload(
        [],
        textContent || null,
        audioFiles,
        getEffectiveSalespersonId(),
      );

      setSuccessMessage(result.message);
      setTextContent('');
      setRecordings([]);
      loadRecent();
    } catch {
      // 401 errors are handled globally by the dumpApi interceptor
      // (clears stale token and reloads to show login page)
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSuccessMessage(null);
    setError(null);
    textareaRef.current?.focus();
  };

  const hasContent =
    textContent.trim() || recordings.length > 0;

  // Format relative time
  const formatTimeAgo = (isoDate: string): string => {
    const diff = Date.now() - new Date(isoDate).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getSourceIcon = (sources: string[] | null): string => {
    if (!sources || sources.length === 0) return '\u{1F4DD}';
    if (sources.includes('voice')) return '\u{1F3A4}';
    if (sources.includes('file')) return '\u{1F4CE}';
    return '\u{1F4DD}';
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.logoMark}>CM</div>
          <div>
            <h1 className={styles.title}>Brain Dump</h1>
            <p className={styles.subtitle}>
              Capture anything - trip reports, call notes, emails, ideas, learnings
            </p>
          </div>
        </header>

        {/* Success state */}
        {successMessage && (
          <div className={styles.successCard}>
            <div className={styles.successIcon}>{'\u2713'}</div>
            <p className={styles.successMessage}>{successMessage}</p>
            <button className={styles.addMoreButton} onClick={handleReset}>
              Add More
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        {/* Input area (hidden when showing success) */}
        {!successMessage && (
          <>
            {/* Salesperson selector */}
            <div className={styles.inputSection}>
              <label className={styles.fieldLabel} htmlFor="salesperson-select">
                Salesperson (optional)
              </label>
              <select
                id="salesperson-select"
                className={styles.selectInput}
                value={selectedSalesperson}
                onChange={(e) => {
                  setSelectedSalesperson(e.target.value);
                  if (e.target.value !== OTHER_SALESPERSON_VALUE) {
                    setCustomSalesperson('');
                  }
                }}
              >
                <option value="">-- Not specified --</option>
                {salespeople.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name}
                  </option>
                ))}
                <option value={OTHER_SALESPERSON_VALUE}>Other...</option>
              </select>
              {selectedSalesperson === OTHER_SALESPERSON_VALUE && (
                <input
                  type="text"
                  className={styles.textInputSmall}
                  placeholder="Enter salesperson name or ID"
                  value={customSalesperson}
                  onChange={(e) => setCustomSalesperson(e.target.value)}
                  autoFocus
                />
              )}
            </div>

            {/* Text input */}
            <div className={styles.inputSection}>
              <textarea
                ref={textareaRef}
                className={styles.textInput}
                placeholder="What's on your mind? Trip report, phone call notes, product insight, customer feedback, meeting takeaway, random idea... anything at all."
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                rows={5}
                autoFocus
              />
            </div>

            {/* Voice recorder */}
            <div className={styles.inputSection}>
              <BrainDumpVoiceRecorder
                recordings={recordings}
                onRecordingsChange={setRecordings}
              />
            </div>

            {/* Large file upload (direct to GCS) */}
            <div className={styles.inputSection}>
              <LargeFileUploader
                textContent={textContent || null}
                salespersonId={getEffectiveSalespersonId()}
                onUploadComplete={handleLargeUploadComplete}
                onError={handleUploadError}
              />
            </div>

            {/* Submit */}
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={!hasContent || isSubmitting}
            >
              {isSubmitting ? (
                <span className={styles.spinner} />
              ) : (
                'Dump It'
              )}
            </button>
          </>
        )}

        {/* Recent submissions toggle */}
        {recentSubmissions.length > 0 && (
          <div className={styles.recentSection}>
            <button
              className={styles.recentToggle}
              onClick={() => setShowRecent(!showRecent)}
            >
              Recent ({recentSubmissions.length})
              <span className={`${styles.chevron} ${showRecent ? styles.chevronOpen : ''}`}>
                {'\u203A'}
              </span>
            </button>

            {showRecent && (
              <div className={styles.recentList}>
                {recentSubmissions.map((sub) => (
                  <div key={sub.id} className={styles.recentItem}>
                    <span className={styles.recentIcon}>
                      {getSourceIcon(sub.content_sources)}
                    </span>
                    <div className={styles.recentDetails}>
                      <span className={styles.recentPreview}>
                        {sub.preview || 'Voice recording / file upload'}
                      </span>
                      <span className={styles.recentTime}>
                        {formatTimeAgo(sub.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
